import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { changePassword, changeMemberOwnPassword } from '../api/license.api';
import Layout from '../components/layout/Layout';
import LicenseMembersSection from '../components/settings/LicenseMembersSection';
import styles from './SettingsPage.module.css';

function maskLicenseKey(key: string): string {
  if (key.length <= 8) return key;
  const visible = 4;
  return key.slice(0, visible) + '****' + key.slice(-visible);
}

export default function AccountSettingsPage() {
  const { licenseKey, deviceId, role, loginId, memberName, isOwner } = useAuth();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState(false);

  const handleChangePassword = async () => {
    if (isChangingPassword) return;
    setPasswordMessage(null);
    setPasswordError(false);

    if (!oldPassword) {
      setPasswordMessage('현재 비밀번호를 입력해주세요.');
      setPasswordError(true);
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMessage('새 비밀번호는 6자 이상이어야 합니다.');
      setPasswordError(true);
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordMessage('새 비밀번호가 일치하지 않습니다.');
      setPasswordError(true);
      return;
    }

    setIsChangingPassword(true);
    try {
      if (isOwner) {
        if (!licenseKey) {
          throw new Error('로그인 정보를 확인할 수 없습니다.');
        }
        await changePassword(licenseKey, oldPassword, newPassword);
      } else {
        // MEMBER 본인 비번 변경 (loginId + currentPassword 인증)
        if (!loginId) {
          throw new Error('로그인 정보를 확인할 수 없습니다.');
        }
        if (newPassword.length < 8) {
          setPasswordMessage('새 비밀번호는 8자 이상이어야 합니다.');
          setPasswordError(true);
          return;
        }
        await changeMemberOwnPassword(loginId, oldPassword, newPassword);
      }
      setPasswordMessage('비밀번호가 변경되었습니다.');
      setPasswordError(false);
      setOldPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setTimeout(() => setPasswordMessage(null), 3000);
    } catch (err) {
      setPasswordMessage(
        err instanceof Error ? err.message : '비밀번호 변경에 실패했습니다.',
      );
      setPasswordError(true);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <Layout title="계정 설정">
      <div className={styles.page}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>로그인 정보</h2>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>역할</span>
            <span className={styles.infoValue}>
              {role === 'MEMBER' ? '추가 관리자 (제한 권한)' : '사장님 (모든 권한)'}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>로그인 ID</span>
            <span className={styles.infoValue}>{loginId ?? '-'}</span>
          </div>
          {memberName && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>이름</span>
              <span className={styles.infoValue}>{memberName}</span>
            </div>
          )}
          {isOwner && (
            <>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>라이선스 키</span>
                <span className={styles.infoValue}>
                  {licenseKey ? maskLicenseKey(licenseKey) : '-'}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>디바이스 ID</span>
                <span className={styles.infoValue}>{deviceId.slice(0, 8)}...</span>
              </div>
            </>
          )}
        </div>

        {/* 매장 추가 관리자 (OWNER 만 표시 / MEMBER 는 안내 문구만) */}
        <LicenseMembersSection />

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>비밀번호 변경</h2>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>현재 비밀번호</span>
            <input
              type="password"
              className={styles.textInput}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="현재 비밀번호"
              autoComplete="current-password"
            />
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>새 비밀번호</span>
            <input
              type="password"
              className={styles.textInput}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="6자 이상"
              autoComplete="new-password"
            />
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>새 비밀번호 확인</span>
            <input
              type="password"
              className={styles.textInput}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              placeholder="새 비밀번호 확인"
              autoComplete="new-password"
            />
          </div>
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleChangePassword}
            disabled={isChangingPassword}
          >
            {isChangingPassword ? '변경 중...' : '비밀번호 변경'}
          </button>
          {passwordMessage && (
            <div
              className={styles.saveMessage}
              style={passwordError ? { color: 'var(--color-danger)' } : undefined}
            >
              {passwordMessage}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
